import type { Action } from '../engine';
import { readJson, writeJson } from './storage';

export type AiSpeed = 'fast' | 'normal' | 'slow';

/**
 * When a chain ends on a pawn that already carries a route: ask each time, always keep the old
 * route, or keep whichever of the two scored more (issue #10: fewer interruptions).
 */
export type InscriptionPolicy = 'ask' | 'keep' | 'better';
export const INSCRIPTION_POLICIES: readonly InscriptionPolicy[] = ['ask', 'keep', 'better'];

/** The look of the game: violet smoke and starlight, or sunlit shallows. */
export type Theme = 'void' | 'sea';
export const THEMES: readonly Theme[] = ['void', 'sea'];

export interface Settings {
  readonly confirmActions: boolean;
  /** null follows the system preference (prefers-reduced-motion). */
  readonly reducedMotion: boolean | null;
  readonly sound: boolean;
  readonly volume: number;
  readonly aiSpeed: AiSpeed;
  readonly inscriptions: InscriptionPolicy;
  readonly flipHotseat: boolean;
  readonly showPreview: boolean;
  readonly telemetry: boolean;
  /** Upload finished and interrupted matches for playtest analysis (src/app/matchLog.ts). */
  readonly shareMatches: boolean;
  readonly highContrast: boolean;
  readonly theme: Theme;
  readonly locale: string;
}

export const DEFAULT_SETTINGS: Settings = Object.freeze({
  confirmActions: false,
  reducedMotion: null,
  sound: true,
  volume: 0.6,
  aiSpeed: 'normal',
  inscriptions: 'ask',
  flipHotseat: true,
  showPreview: true,
  telemetry: false,
  shareMatches: true,
  highContrast: false,
  theme: 'void',
  locale: 'en',
});

const KEY = 'settings.v1';

/** The browser bar color of each theme, the color at the top of its background. */
export const THEME_COLORS: Readonly<Record<Theme, string>> = { void: '#0b0918', sea: '#d9eeea' };

/** Settings read back from storage: unknown or invalid values fall back to the defaults. */
export function parseSettings(v: unknown): Settings | null {
  if (typeof v !== 'object' || v === null) return null;
  const s = v as Partial<Record<keyof Settings, unknown>>;
  const bool = (x: unknown, d: boolean) => (typeof x === 'boolean' ? x : d);
  return {
    confirmActions: bool(s.confirmActions, DEFAULT_SETTINGS.confirmActions),
    reducedMotion: typeof s.reducedMotion === 'boolean' ? s.reducedMotion : null,
    sound: bool(s.sound, DEFAULT_SETTINGS.sound),
    volume:
      typeof s.volume === 'number' && s.volume >= 0 && s.volume <= 1
        ? s.volume
        : DEFAULT_SETTINGS.volume,
    aiSpeed:
      s.aiSpeed === 'fast' || s.aiSpeed === 'slow' || s.aiSpeed === 'normal' ? s.aiSpeed : 'normal',
    inscriptions: INSCRIPTION_POLICIES.includes(s.inscriptions as InscriptionPolicy)
      ? (s.inscriptions as InscriptionPolicy)
      : DEFAULT_SETTINGS.inscriptions,
    flipHotseat: bool(s.flipHotseat, DEFAULT_SETTINGS.flipHotseat),
    showPreview: bool(s.showPreview, DEFAULT_SETTINGS.showPreview),
    telemetry: bool(s.telemetry, DEFAULT_SETTINGS.telemetry),
    shareMatches: bool(s.shareMatches, DEFAULT_SETTINGS.shareMatches),
    highContrast: bool(s.highContrast, DEFAULT_SETTINGS.highContrast),
    theme: THEMES.includes(s.theme as Theme) ? (s.theme as Theme) : DEFAULT_SETTINGS.theme,
    locale: typeof s.locale === 'string' ? s.locale : 'en',
  };
}

export const loadSettings = (): Settings => readJson(KEY, parseSettings) ?? DEFAULT_SETTINGS;

export const saveSettings = (s: Settings): string | null => writeJson(KEY, s);

/**
 * Delay before a computer move, so each move can be seen. Links, Epitaph replays and the end of
 * a chain wait longer: a chain is followed link by link, each seen before the next is played.
 */
export const aiDelayMs = (speed: AiSpeed, reducedMotion: boolean, action?: Action): number => {
  const base = reducedMotion
    ? { fast: 60, normal: 300, slow: 700 }[speed]
    : { fast: 150, normal: 700, slow: 1200 }[speed];
  const move =
    action?.type === 'PLAY_LINK' || action?.type === 'PLAY_EPITAPH' || action?.type === 'END_CHAIN';
  return move ? Math.round(base * 1.6) : base;
};

export function prefersReducedMotion(): boolean {
  try {
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

export const effectiveReducedMotion = (s: Settings): boolean =>
  s.reducedMotion ?? prefersReducedMotion();
